import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import {
  loadPrefs,
  shouldNudgeNow,
  pickNudge,
  showSystemNudge,
  recordShown,
  permissionStatus,
} from "@/lib/nudges";
import { WELCOME_BANNER } from "@/constants/testIds";

/**
 * NudgeWhisper — a calm, dismissible card that surfaces a nudge on the
 * Welcome page when the user returns and a soft phrase is "due" per their
 * settings. Honors frequency cap, daily de-dupe, and the on/off toggle.
 *
 * If notification permission is granted, also fires a system notification
 * with the same phrase. Otherwise just the card is shown — never both noisily.
 */
export default function NudgeWhisper() {
  const [phrase, setPhrase] = useState(null);

  useEffect(() => {
    const prefs = loadPrefs();
    if (!shouldNudgeNow(prefs)) return;
    const text = pickNudge(prefs);
    setPhrase(text);
    // Attempt a (silent) system notification too, only if permission granted.
    if (permissionStatus() === "granted") {
      showSystemNudge(text);
    }
    recordShown(prefs);
  }, []);

  if (!phrase) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid={WELCOME_BANNER.nudge}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="relative w-full overflow-hidden rounded-3xl p-4 mb-5"
        style={{
          background:
            "linear-gradient(135deg, rgba(74,32,64,0.05) 0%, rgba(117,55,66,0.05) 100%)",
          border: "1px solid rgba(74,32,64,0.10)",
        }}
      >
        <div className="flex items-start gap-3">
          <Sparkles
            size={16}
            className="mt-1 shrink-0"
            style={{ color: "var(--un-burgundy)" }}
          />
          <p
            className="flex-1 italic text-[16px] leading-snug"
            style={{
              color: "var(--un-text-1)",
              fontFamily: "Cormorant Garamond, serif",
              fontSize: "17px",
            }}
          >
            {phrase}
          </p>
          <button
            type="button"
            aria-label="dismiss"
            onClick={() => setPhrase(null)}
            className="un-press p-1 shrink-0"
          >
            <X size={14} style={{ color: "var(--un-text-3)" }} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
